import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["sticky", "__scopeDialog"],
  _excluded2 = ["size", "swipeToDismissEnaled", "overlay", "className", "onAnimationEnd", "onAnimationStart", "forceMount", "children", "overlayProps", "portalProps", "style"],
  _excluded3 = ["className"],
  _excluded4 = ["container"],
  _excluded5 = ["isDragging", "transform3DValues"],
  _excluded6 = ["__scopeDialog"],
  _excluded7 = ["__scopeDialog"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable max-lines */
import { clsx } from 'clsx';
import * as React from 'react';
import { RoundButton } from '../../../button';
import { DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle, DialogTrigger } from '../../../headless';
import { DomPrimitive } from '../../../internal/components/DomPrimitive';
import { createContext } from '../../../internal/context/scoped-context';
import { useComposedRefs } from '../../../internal/hooks/useComposedRefs';
import { composeEventHandlers } from '../../../internal/utils/composeEventHandlers';
import { useDeviceType } from '../../../responsive';
import { ArticleHeading1, ArticleHeading2, ArticleParagraph1 } from '../../../typography';
import { useSwipeToDismiss } from '../../internal/hooks/useSwipeToDismiss';
import * as styles from './Modal.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const CLOSE_BUTTON_NAME = 'ModalCloseButton';
const CONTENT_NAME = 'ModalContent';
const TITLE_NAME = 'ModalTitle';
const TRIGGER_NAME = 'ModalTrigger';
const DESCRIPTION_NAME = 'ModalTitle';
const HEADER_NAME = 'ModalHeader';
const FOOTER_NAME = 'ModalFooter';
const BODY_NAME = 'ModalBody';
const [ModalContextProvider, useModalContext] = createContext('Modal');

/**
 * Компонент для рендера кнопки открытия модального окна
 */

export const ModalTrigger = /*#__PURE__*/React.forwardRef((props, ref) => /*#__PURE__*/_jsx(DialogTrigger, _objectSpread(_objectSpread({}, props), {}, {
  ref: ref
})));
ModalTrigger.displayName = TRIGGER_NAME;

/**
 * Компонент для рендера кнопки закрытия модального окна
 */

export const ModalClose = DialogClose;

/**
 * Компонент для рендера плавающей кнопки закрытия модального окна
 */

export const ModalCloseButton = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      sticky = true,
      __scopeDialog
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    className: clsx(styles['close'], sticky && styles['close-sticky']),
    children: /*#__PURE__*/_jsx(DomPrimitive.div, {
      className: styles['close-inner'],
      children: /*#__PURE__*/_jsx(DialogClose, {
        __scopeDialog: __scopeDialog,
        asChild: true,
        children: /*#__PURE__*/_jsx(RoundButton, _objectSpread(_objectSpread({}, props), {}, {
          ref: ref
        }))
      })
    })
  });
});
ModalCloseButton.displayName = CLOSE_BUTTON_NAME;

/**
 * Компонент для рендера контекста модального окна
 */

export function ModalRoot(props) {
  return /*#__PURE__*/_jsx(ModalContextProvider, _objectSpread(_objectSpread({}, props), {}, {
    children: /*#__PURE__*/_jsx(DialogRoot, _objectSpread({}, props))
  }));
}

/**
 * Компонент для рендера контента модального окна
 */
const DEFAULT_OVERLAY_PROPS = {};
const DEFAULT_PORTAL_PROPS = {};
export const ModalContent = /*#__PURE__*/React.forwardRef((_ref2, forwardedRef) => {
  let {
      size = 'M',
      swipeToDismissEnaled = true,
      overlay,
      className,
      onAnimationEnd,
      onAnimationStart,
      forceMount,
      children,
      overlayProps,
      portalProps,
      style: contentStyles
    } = _ref2,
    restContentProps = _objectWithoutProperties(_ref2, _excluded2);
  const _ref3 = overlayProps || DEFAULT_OVERLAY_PROPS,
    {
      className: overlayClassName
    } = _ref3,
    restOverlayProps = _objectWithoutProperties(_ref3, _excluded3);
  const _ref4 = portalProps || DEFAULT_PORTAL_PROPS,
    {
      container: portalContainer
    } = _ref4,
    restPortalProps = _objectWithoutProperties(_ref4, _excluded4);
  const [isAnimating, setAnimating] = React.useState(false);
  const contentRef = React.useRef(null);
  const ref = useComposedRefs(forwardedRef, contentRef);
  const context = useModalContext(CONTENT_NAME);
  const deviceType = useDeviceType();
  const _useSwipeToDismiss = useSwipeToDismiss({
      onDismiss: transformValues => {
        var _contentRef$current, _context$onOpenChange;
        const translateY = transformValues[1];

        /**
         * Устанавливаем значение --initial-translate, чтобы анимация slide-down
         * для закрытия мобильной модалки отработала корректно, относительно текущего translateY
         */
        (_contentRef$current = contentRef.current) === null || _contentRef$current === void 0 || _contentRef$current.style.setProperty('--initial-translate', typeof translateY === 'number' ? "".concat(translateY, "px") : translateY);
        (_context$onOpenChange = context.onOpenChange) === null || _context$onOpenChange === void 0 || _context$onOpenChange.call(context, false);
      },
      enabled: swipeToDismissEnaled && deviceType === 'phone',
      transformElementRef: contentRef
    }),
    {
      isDragging,
      transform3DValues
    } = _useSwipeToDismiss,
    eventListeners = _objectWithoutProperties(_useSwipeToDismiss, _excluded5);
  const style = React.useMemo(() => {
    if (isDragging) {
      return _objectSpread(_objectSpread({}, contentStyles), {}, {
        transform: "translate3d(".concat(transform3DValues.join(','), ")")
      });
    }
    return contentStyles;
  }, [contentStyles, isDragging, transform3DValues]);

  /**
   * На моб устройствах используем css анимацию transofrmY (slide down и slide up)
   * чтобы не было мерцания скролбара при открытии/закрытии модалки
   * добавляем класс устанавливающий overflow: hidden на wrapper во время анимации
   */
  const handleAnimationStart = React.useMemo(() => composeEventHandlers(onAnimationStart, () => setAnimating(true)), [onAnimationStart]);
  const handleAnimationEnd = React.useMemo(() => composeEventHandlers(onAnimationEnd, () => {
    var _contentRef$current2;
    setAnimating(false);
    /**
     * Сбрасываем переменную значение --initial-translate для анимации закрытия мобильной модалки
     */
    (_contentRef$current2 = contentRef.current) === null || _contentRef$current2 === void 0 || _contentRef$current2.style.setProperty('--initial-translate', '0');
  }), [onAnimationEnd]);
  return /*#__PURE__*/_jsx(DialogPortal, _objectSpread(_objectSpread({
    container: portalContainer,
    forceMount: restOverlayProps.forceMount || forceMount
  }, restPortalProps), {}, {
    children: /*#__PURE__*/_jsx(DialogOverlay, _objectSpread(_objectSpread({
      className: clsx(overlayClassName, styles['overlay'], overlay === 'transparent' && styles['overlay-transparent'], overlay === 'blured' && styles['overlay-blured'])
    }, restOverlayProps), {}, {
      children: /*#__PURE__*/_jsx("div", {
        className: clsx(styles['content-wrapper'], (isAnimating || isDragging) && styles['content-wrapper-animating']),
        children: /*#__PURE__*/_jsx("div", {
          className: styles['content-inner'],
          children: /*#__PURE__*/_jsxs(DialogContent, _objectSpread(_objectSpread({
            ref: ref,
            forceMount: forceMount,
            onAnimationStart: handleAnimationStart,
            onAnimationEnd: handleAnimationEnd,
            className: clsx(styles['content'], styles["content-".concat(size.toLocaleLowerCase())], className),
            style: style
          }, restContentProps), {}, {
            children: [deviceType === 'phone' && /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread({
              className: clsx(styles['swipe-handle'])
            }, eventListeners)), children]
          }))
        })
      })
    }))
  }));
});
ModalContent.displayName = CONTENT_NAME;

/**
 * Компонент для рендера заголовка модального окна
 */

export const ModalTitle = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const deviceType = useDeviceType();
  const {
      __scopeDialog
    } = props,
    rest = _objectWithoutProperties(props, _excluded6);
  const Heading = React.useMemo(() => {
    switch (deviceType) {
      case 'phone':
        return ArticleHeading2;
      default:
        return ArticleHeading1;
    }
  }, [deviceType]);
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    className: styles['title'],
    children: /*#__PURE__*/_jsx(DialogTitle, {
      __scopeDialog,
      asChild: true,
      children: /*#__PURE__*/_jsx(Heading, _objectSpread(_objectSpread({}, rest), {}, {
        ref: forwardedRef
      }))
    })
  });
});
ModalTitle.displayName = TITLE_NAME;

/**
 * Компонент для рендера описания модального окна
 * описание может использоваться как в заголовке так и в теле модального окна
 */

export const ModalDescription = /*#__PURE__*/React.forwardRef((_ref5, forwardedRef) => {
  let {
      __scopeDialog
    } = _ref5,
    props = _objectWithoutProperties(_ref5, _excluded7);
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    className: styles['description'],
    children: /*#__PURE__*/_jsx(DialogDescription, {
      __scopeDialog,
      asChild: true,
      children: /*#__PURE__*/_jsx(ArticleParagraph1, _objectSpread(_objectSpread({}, props), {}, {
        ref: forwardedRef
      }))
    })
  });
});
ModalDescription.displayName = DESCRIPTION_NAME;

/**
 * Компонент для рендера контейнера заголовка модального окна
 */

export const ModalHeader = /*#__PURE__*/React.forwardRef((_ref6, forwardedRef) => {
  let {
    children,
    stycky = true
  } = _ref6;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: clsx(styles['header'], stycky && styles['header-sticky']),
    children: children
  });
});
ModalHeader.displayName = HEADER_NAME;

/**
 * Компонент для рендера контейнера футера модального окна
 */

export const ModalFooter = /*#__PURE__*/React.forwardRef((_ref7, forwardedRef) => {
  let {
    children,
    sticky = true
  } = _ref7;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: clsx(styles['footer'], sticky && styles['footer-sticky']),
    children: children
  });
});
ModalFooter.displayName = FOOTER_NAME;

/**
 * Компонент контейнер тела модального окна
 */

export const ModalBody = /*#__PURE__*/React.forwardRef((_ref8, forwardedRef) => {
  let {
    children
  } = _ref8;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: styles['body'],
    children: children
  });
});
ModalBody.displayName = BODY_NAME;
export { ModalBody as Body, ModalClose as Close, ModalCloseButton as CloseButton, ModalContent as Content, ModalDescription as Description, ModalFooter as Footer, ModalHeader as Header, ModalRoot as Root, ModalTitle as Title, ModalTrigger as Trigger };